import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { locationSalesGetSalesByMenu, type SalesByMenuEntry } from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type ExtendedMenu = SalesByMenuEntry;

interface MenuViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const MenuView = ({ locationId, dateRange, tableId }: MenuViewProps) => {
  const { data, isPending } = useQuery(
    convexQuery(locationSalesGetSalesByMenu, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const columnHelper = createColumnHelper<ExtendedMenu>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("menuName", {
          header: "Menu Name",
          cell: ({ getValue }) => getValue(),
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.some((filter) =>
              original.menuName.toLowerCase().includes(filter.toLowerCase())
            ),
        }),
        columnHelper.accessor("orderType", {
          header: "Order Type",
          cell: ({ getValue }) => getValue(),
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.orderType),
          meta: {
            faceting: {
              title: "Order Type",
              getOption: (orderType: ExtendedMenu["orderType"]) => ({
                value: orderType,
                label: orderType,
              }),
            },
          },
        }),
        columnHelper.accessor("itemsSold", {
          header: "Items Sold",
          cell: ({ getValue }) => getValue().toLocaleString(),
          meta: {
            getCellForExport: (value: ExtendedMenu["itemsSold"]) => value,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (value: ExtendedMenu["amountSold"]) => value,
          },
        }),
        columnHelper.accessor("shareOfTotalSales", {
          header: "% of Total Sales",
          cell: ({ getValue }) => `${(getValue() * 100).toFixed(1)}%`,
          meta: {
            getCellForExport: (value: ExtendedMenu["shareOfTotalSales"]) =>
              value,
          },
        }),
      ] as ColumnDef<ExtendedMenu>[],
    [columnHelper]
  );

  if (isPending || !data) {
    return <LoadingPanel message="Loading menu performance data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
